import axios from 'axios';
import { dsarFallbackFilename, filenameFromContentDisposition } from '../components/dsarDocument';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Use explicit backend URL for all requests
const client = axios.create({
  baseURL: API_BASE_URL, // Explicit backend URL
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000 // 30 second timeout
});

// Add auth token to requests
client.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  console.log('Request config with auth:', {
    url: config.url,
    hasToken: !!token,
    hasHeaders: !!config.headers
  });
  
  return config;
});

// Handle auth errors
client.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// types
export interface EmailCorrespondence {
  id: string;
  type: 'email';
  direction: 'sent' | 'received';
  subject: string;
  from: string;
  to: string[];
  cc?: string[];
  timestamp: string;
  bodyPreview: string;
  body?: string; // Full email body
  hasAttachments: boolean;
  s3Location?: string;
  source?: string; // 'email-reply-tracking' for auto-tracked emails, 'client_resend_signed' for signed resend docs
  autoGenerated?: boolean;
  actor?: string;
  templateId?: string;
  templateSnapshotId?: string;
  templateName?: string;
  templateImagePath?: string; // Screenshot URL for template preview
  status?: 'signed' | string; // For signed document correspondence entries
  attachmentUrl?: string; // Direct URL to attachment for signed docs
  attachmentName?: string; // Name of attached file
  metadata?: Record<string, unknown>;
  documents?: Array<{
    label?: string;
    url?: string;
    blobName?: string;
    type?: string;
  }>;
  attachments?: Array<{
    filename?: string;
    name?: string;
    url?: string;
    blob_name?: string;
    size?: number;
    content_type?: string;
  }>;
}

export interface PhoneCorrespondence {
  id: string;
  type: 'phone';
  direction: 'incoming' | 'outgoing';
  subject: string;
  from: string;
  to: string;
  timestamp: string;
  callDuration: number; // in seconds
  callAnswered: boolean;
  callNotes: string;
  bodyPreview: string; // First part of call notes for preview
  units?: number; // Number of billable units (1 unit per 6 minutes)
}

export interface MemoCorrespondence {
  id: string;
  type: 'memo';
  title: string;
  content?: string;
  timestamp: string;
  actor?: string;
  source?: string;
  autoGenerated?: boolean;
  documents?: Array<{
    label?: string;
    url?: string;
    blobName?: string;
    type?: string;
  }>;
  metadata?: Record<string, unknown>;
}

export type Correspondence = EmailCorrespondence | PhoneCorrespondence | MemoCorrespondence;

export interface GeoIpResult {
  ip: string;
  country?: string | null;
  state?: string | null;
  city?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export type KeyDateStatus = 'empty' | 'recorded' | 'overdue' | 'dueSoon' | 'upcoming';

export type LocKeyDateField =
  | 'limitation'
  | 'locSent'
  | 'responseDue'
  | 'provisionalDecision'
  | 'objectionDue'
  | 'lenderResponse'
  | 'fosDeadlineDenied'
  | 'fosDeadlineNoResponse';

export type DsarKeyDateField =
  | 'limitation'
  | 'dsarSent'
  | 'due'
  | 'dueWithExtension'
  | 'extensionRequested'
  | 'dsarResponse'
  | 'breachLocSent'
  | 'issueProceedings';

export interface KeyDateEntryView {
  value: string | null;
  manualValue: string | null;
  defaultValue: string | null;
  isManual: boolean;
  notes?: string | null;
  sourceId?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  extra?: Record<string, any> | null;
  status: KeyDateStatus;
  daysUntil?: number | null;
}

export interface KeyDatesSummaryEntry {
  path: string;
  label: string;
  value: string;
  daysUntil: number;
  status: KeyDateStatus;
}

export interface ClaimKeyDatesView {
  loc: Record<LocKeyDateField, KeyDateEntryView>;
  dsar: Record<DsarKeyDateField, KeyDateEntryView>;
  summary: {
    overdue: KeyDatesSummaryEntry[];
    dueSoon: KeyDatesSummaryEntry[];
    upcoming: KeyDatesSummaryEntry[];
  };
  counts: Record<string, number>;
  hasManualOverrides: boolean;
  meta: {
    lastUpdatedAt?: string | null;
    lastUpdatedBy?: string | null;
    version?: number;
    generatedAt: string;
  };
}

export type KeyDatesUpdateEntry = {
  value?: string | null;
  notes?: string | null;
  sourceId?: string | null;
  outcome?: string | null;
  reason?: string | null;
};

export interface KeyDatesUpdatePayload {
  loc?: Partial<Record<LocKeyDateField, KeyDatesUpdateEntry | null>>;
  dsar?: Partial<Record<DsarKeyDateField, KeyDatesUpdateEntry | null>>;
}

export interface ClaimKeyDatesResponse {
  claimId: string;
  keyDates: ClaimKeyDatesView;
  changes?: string[];
  message?: string;
}

export interface LenderBulkSubmissionAncillaryEntry {
  cost: string | null;
  provider: string | null;
  commission: string | null;
}

export interface LenderBulkSubmissionDetails {
  agreement: {
    dateOfInception: string | null;
    dateOfSettlement: string | null;
    vin: string | null;
    financeProductType: string | null;
    financeProductCode: string | null;
    agreementTemplateReference: string | null;
    retailerName: string | null;
    financeProgrammeName: string | null;
  };
  broker: {
    independentBroker: string | null;
    commissionDisclosureProvided: string | null;
    commissionDisclosureEvidence: string | null;
    subsequentDisclosure: string | null;
  };
  financials: {
    vehiclePrice: string | null;
    salePrice: string | null;
    depositPaid: string | null;
    creditAdvanced: string | null;
    apr: string | null;
    interestRate: string | null;
    minInterestRate: string | null;
    maxInterestRate: string | null;
    minRateNotOfferedEvidence: string | null;
    monthlyRepaymentAmount: string | null;
    repaymentLength: string | null;
    totalAmountPayable: string | null;
    balloonPaymentAmount: string | null;
    guaranteedFutureValue: string | null;
  };
  commission: {
    dealerCommission: string | null;
    brokerCommission: string | null;
    commissionPercentage: string | null;
    commissionPaymentDate: string | null;
    commissionClawbackStatus: string | null;
    structurePart1: string | null;
    structurePart2: string | null;
    structurePart3: string | null;
    structurePart4: string | null;
    additionalIncentives: string | null;
    incentivesRecipient: string | null;
  };
  relationships: {
    commercialTie: string | null;
    suitabilityStatementProvided: string | null;
    affordabilityApprovalMethod: string | null;
    vulnerabilityAssessment: string | null;
  };
  ancillaryProducts: {
    gap: LenderBulkSubmissionAncillaryEntry;
    ppi: {
      amount: string | null;
      provider: string | null;
      commission: string | null;
    };
    extendedWarranty: {
      amount: string | null;
      provider: string | null;
      commission: string | null;
    };
    serviceMaintenance: LenderBulkSubmissionAncillaryEntry;
    paintFabric: LenderBulkSubmissionAncillaryEntry;
  };
  extras: {
    fuelContribution: string | null;
    cashbackIncentives: string | null;
  };
  followUp: {
    contactedConsumer: string | null;
    duplicateClaim: string | null;
    duplicateEvidence: string | null;
  };
  meta: {
    lastUpdatedAt: string | null;
    lastUpdatedBy: string | null;
    version: number;
  };
}

export interface LenderBulkSubmissionResponse {
  claimId: string;
  lenderBulkSubmission: LenderBulkSubmissionDetails;
  meta: {
    lastUpdatedAt?: string | null;
    lastUpdatedBy?: string | null;
    version?: number;
    generatedAt: string;
  };
  changes?: string[];
  message?: string;
}

export type LenderBulkSubmissionUpdatePayload = Partial<{
  agreement: Partial<LenderBulkSubmissionDetails['agreement']>;
  broker: Partial<LenderBulkSubmissionDetails['broker']>;
  financials: Partial<LenderBulkSubmissionDetails['financials']>;
  commission: Partial<LenderBulkSubmissionDetails['commission']>;
  relationships: Partial<LenderBulkSubmissionDetails['relationships']>;
  ancillaryProducts: {
    gap?: Partial<LenderBulkSubmissionDetails['ancillaryProducts']['gap']>;
    ppi?: Partial<LenderBulkSubmissionDetails['ancillaryProducts']['ppi']>;
    extendedWarranty?: Partial<LenderBulkSubmissionDetails['ancillaryProducts']['extendedWarranty']>;
    serviceMaintenance?: Partial<LenderBulkSubmissionDetails['ancillaryProducts']['serviceMaintenance']>;
    paintFabric?: Partial<LenderBulkSubmissionDetails['ancillaryProducts']['paintFabric']>;
  };
  extras: Partial<LenderBulkSubmissionDetails['extras']>;
  followUp: Partial<LenderBulkSubmissionDetails['followUp']>;
}>;

export interface DuplicationAudit {
  hasInstructedOtherFirm: 'yes' | 'no' | 'not_sure';
  otherFirmNames?: string;
  firstInstructionDate?: string;
  hasCancelledFirm?: 'yes' | 'no' | null;
  cancelledFirmDetails?: string;
  claimFactsConfirmed: boolean;
  claimFactsSummary?: string;
  notes?: string;
  completedBy?: string;
  completedAt?: string;
  lastUpdatedBy?: string;
  lastUpdatedAt?: string;
}

/** A lender's response to our submission, imported from a lender batch file.
 * `status` = 'matched' when the lender returned agreement data, 'exception'
 * when they could not (see exceptionReason, e.g. "No records found"). */
export interface LenderResponse {
  source?: string;              // e.g. "Complex Law - Batch 1.2"
  lender?: string;
  /** The LENDER receiving OUR submission — NOT us receiving their answer.
   * Straight from the workbook's "Date Received" column. On Batch 1.2 it is
   * 2026-02-20 against a submissionSentDate of 2026-02-21, i.e. a day before we
   * sent it, which is how the misreading gets caught. When WE received the
   * response is `submissionOutcomeDate` on the claim. Absent on exception rows:
   * that sheet has no date column. */
  receivedDate?: string;
  status?: 'matched' | 'exception';
  exceptionReason?: string;
  agreementNumber?: string;
  vehicleRegistration?: string;
  agreementStartDate?: string;
  agreementEndDate?: string;
  agreementEarlyEndDate?: string;
  apr?: number | string;
  agreementTerm?: number | string;
  vehicleCashPrice?: number | string;
  financedAmount?: number | string;
  monthlyRentals?: number | string;
  balloonPayment?: number | string;
  commissionModel?: string;
  // Commission breakdown, from the Santander 30-Jun-2026 response onwards.
  // Batch 1.2 carries none of these, and Santander carries none of the vehicle
  // or payment fields above — which is why LenderResponseCard renders only the
  // columns a given claim actually has values for. Numbers here (Batch 1.2
  // stored money as strings), so the four can be summed; fmtMoney takes both.
  increasingDicCommission?: number | string | null;
  scaleCommission?: number | string | null;
  fixedCommission?: number | string | null;
  cpbCommission?: number | string | null;
  additionalCommission?: string;   // 'Y' | 'N' | ''
  // Business minimum data set for the recurring lender-response template
  // (24 Aug 2026) — written by the admin import page.
  additionalCommissionAmount?: number | string | null;
  totalCommission?: number | string | null;
  totalCostOfCredit?: number | string | null;
  /** Unmapped spreadsheet columns, captured verbatim by the import
   * ("import everything they give us") — header -> cell text. */
  additionalData?: Record<string, string>;
  importedAt?: string;
  importedBy?: string;
  /** Which admin import run wrote this entry — traceable to the exact
   * stored spreadsheet it came from. */
  importRunId?: string;
}

export type LOAIdentityReviewState =
  | 'staging'
  | 'pending_review'
  | 'approved'
  | 'processing'
  | 'completed'
  | 'rejected'
  | 'processing_failed';

export interface LOAIdentityName {
  title?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  secondName?: string;
  [key: string]: unknown;
}

export interface LOAIdentityAddress {
  line1?: string;
  line2?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  town?: string;
  county?: string;
  postcode?: string;
  [key: string]: unknown;
}

export interface LOAIdentityReviewRecord {
  requestId: string;
  primaryClaimId: string;
  claimIds: string[];
  clientId?: string;
  state: LOAIdentityReviewState;
  submissionHash: string;
  submission: {
    currentNames?: LOAIdentityName[];
    fullLegalName?: string;
    dateOfBirth?: string;
    currentAddress?: LOAIdentityAddress;
    previousNames?: Array<LOAIdentityName | string>;
    previousAddresses?: Array<LOAIdentityAddress | string>;
    vehicles?: Array<{
      claimId?: string;
      make?: string;
      model?: string;
      registration?: string;
      [key: string]: unknown;
    }>;
    consent?: Record<string, unknown>;
    [key: string]: unknown;
  };
  originals: Record<string, {
    title?: string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    secondName?: string;
    fullLegalName?: string;
    dateOfBirth?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    county?: string;
    postcode?: string;
    previousNames?: Array<LOAIdentityName | string>;
    previousAddresses?: Array<LOAIdentityAddress | string>;
    [key: string]: unknown;
  }>;
  reasons: Record<string, string[]>;
  decision?: { actor?: string; reason?: string; at?: string };
  processedClaimIds?: string[];
}

export interface LOAIdentityEvidenceAttachment {
  name?: string;
  filename?: string;
  url?: string;
  downloadUrl?: string;
  blobName?: string;
  [key: string]: unknown;
}

export interface LOAIdentityEvidence {
  status: string;
  verifiedAt?: string;
  notes?: string;
  attachments: LOAIdentityEvidenceAttachment[];
  taskId?: string;
}

export interface LOAIdentityReviewsResponse {
  reviews: LOAIdentityReviewRecord[];
  idEvidence: Record<string, LOAIdentityEvidence>;
}

export interface Claim {
  id: string;
  email: string;
  /** The recorded cancellation-fee decision (fee_waiver_service). Present
   * only once a member of staff has waived -- or explicitly un-waived --
   * the fee after a ring-fence review call. `basis` is why the claim was
   * eligible for the decision ('age-70-plus' / 'age-unknown' /
   * 'staff-discretion'); `reason` is the staff member's reason from the
   * closed WAIVER_REASONS vocabulary. Recording one does NOT void an
   * existing Stripe payment link. */
  feeWaiver?: {
    waived: boolean;
    reason: string;
    note: string;
    waivedBy: string;
    waivedAt: string;
    basis: string;
  };
  firstName?: string;
  lastName?: string;
  status?: 'ACTIVE' | 'CANCELLED' | 'COMPLETED' | 'OPEN' | 'REOPENED' | string;
  addressLine1?: string;
  addressLine2?: string; // Add this property
  town?: string; // Add this property
  county?: string; // Add this property
  postcode?: string; // Add this property
  phoneNumber?: string; // Add this property
  locSentDate?: string | null;
  responseDate?: string | null;
  createdInApiAt?: string; // Keep existing fields
  formOfAuthorityUrl?: string;
  finalBillUrl?: string;
  damageBasedAgreementUrl?: string;
  locDocUrl?: string;
  // History markers preserved when cancel/reinstate clears locSentDate/locDocUrl.
  // Needed so cancelled claims that did have an LOC don't read as "Not Sent" in the UI.
  locPreviousSentDate?: string | null;
  locPreviousDocUrl?: string;
  locWithdrawalSentToLender?: boolean;
  // Add cancellation fields
  cancellationDate?: string;
  cancellationSource?: string;
  cancellationUpdatedBy?: string;
  cancellationReason?: string;
  finalResponseClosure?: {
    state: 'pending' | 'sending' | 'accepted' | 'failed' | 'unknown';
    operationId?: string;
    reasonLabels?: string[];
    recipient?: string;
  };
  nonCooperationReasons?: Array<{
    code: string;
    label: string;
  }>;
  cancellationPaymentStatus?: string;
  cancellationPaymentRequestedAt?: string;
  cancellationPaymentPaidAt?: string;
  /** Set when the cancellation fee was voided (#591: who and why). */
  cancellationPaymentVoidedAt?: string;
  cancellationPaymentVoidedBy?: string;
  cancellationPaymentVoidReason?: string;
  cancellationPaymentId?: string;
  cancellationPaymentLinkUrl?: string;
  cancellationPaymentAmountTotal?: number;
  cancellationPaymentAmountPerClaim?: number;
  cancellationPaymentExpiresAt?: string;
  cancellationReinstateToken?: string;
  cancellationManualPayment?: {
    amountPaid: number;
    expectedAmount: number;
    outstandingBalance: number;
    isFinal: boolean;
    recordedBy: string;
    recordedAt: string;
    notes?: string;
  };
  // File management
  fileCount?: number;
  lastFileUploadDate?: string;
  
  // Email correspondence
  correspondence?: Correspondence[];
  lastCorrespondenceDate?: string;
  lastFileReviewDate?: string;

  // Units tracking
  unitCount?: number;
  unitHistory?: Array<{
    timestamp?: string;
    delta?: number;
    total?: number;
    reason?: string;
    actor?: string;
    source?: string;
    correspondenceId?: string;
  }>;
  
  // Email reply tracking
  trackingId?: string;
  replyToAddress?: string;
  replyCount?: number;
  lastReplyReceived?: string;
  
  // Client ID for linking to client record
  clientId?: string;

  // Client conflict information (populated from client record)
  clientConflictInfo?: {
    conflictFound?: boolean;
    conflictMarkedAt?: string;
    lastUpdated?: string;
  };

  // User ID
  userId?: string;
  claimId?: string;
  title?: string;
  city?: string;
  dateOfBirth?: string;              // format "DD/MM/YYYY"
  agreement_dateAgreed?: string;     // format "DD/MM/YYYY HH:mm"

  craFinanceLender?: string;
  craAgreementNumberOriginal?: string;
  craCompanyType?: string;
  craCreditStartDate?: string;       // format "DD/MM/YYYY"
  craCreditEndDate?: string;         // format "DD/MM/YYYY"
  vehicleRegistration?: string;
  defendantReference?: string;
  commissionAmount?: number;
  commissionType?: string;
  /** Date the commission disclosure reached us. The UI derives the
   * three-year limitation deadline from this factual start date. */
  commissionInformedDate?: string;

  craTitle?: string;
  craFirstName?: string;
  craLastName?: string;
  craAgreementAddressLine1?: string;
  craAgreementAddressLine2?: string;
  craAgreementPostcode?: string;
  craDateOfBirth?: string;           // format "DD/MM/YYYY"

  craCheckIdentityOutcome?: string;
  craAMLCheck?: string;
  craAMLDecisionReason?: string;
  craCaseType?: string;
  craClassificationStatus?: string;
  craClassificationReason?: string;
  craCheckLendersReturned?: string | null;
  craCheckAttempted?: string | null;

  clientIpAddress?: string;
  utmSource?: string;

  previousAddresses?: string[];
  previouslyKnownNames?: string[];

  reinstatementDisabled?: boolean;
  /** Set by the backend on cancelled claims outside the FCA redress scheme
   * (redress_reinstatement_guard): the CRA agreement date, or the lender's
   * bulk-submission response, puts it outside 6 Apr 2007 - 1 Nov 2024. Unlike
   * reinstatementDisabled there is no override -- the Reinstate button is
   * greyed out and every reinstate route refuses. null/absent = allowed. */
  reinstatementBlock?: {
    code: string;
    message: string;
    basis: string;
    detail: string;
  } | null;

  doNotEnforce?: boolean;
  doNotEnforceReason?: string;
  doNotEnforceNotes?: string;
  doNotEnforceSetBy?: string;
  doNotEnforceSetAt?: string;

  proclaimBaseId?: string;
  proclaimSuffix?: string;
  proclaimReference?: string;

  valid8responseUrl?: string;
  amlCheckUrl?: string;
  amlcheckUrl?: string;

  isDownloaded?: boolean;

  // Cosmos DB metadata
  _rid?: string;
  _self?: string;
  _etag?: string;
  _attachments?: string;
  _ts?: number;
  
  // Audit fields
  lastAuditDate?: string;
  auditCount?: number;
  hasAudit?: boolean;
  audits?: Array<{
    auditId: string;
    auditedBy: string;
    auditedAt: string;
  }>;
  
  // Legacy claim indicator
  isLegacyClaim?: boolean;

  // Values kept from the system a claim was imported from. FC/FM claims
  // carry their original Proclaim claim ID (e.g. RSL6295U) here.
  legacyMetadata?: {
    originalClaimId?: string | null;
    originalClientId?: string | null;
    [key: string]: unknown;
  } | null;
  
  // ID verification fields
  idVerified?: boolean;
  idDocumentUrl?: string;

  // Client resend signed documents
  foaResendSignedAt?: string;
  foaResendSignedUrl?: string;
  dbaResendSignedAt?: string;
  dbaResendSignedUrl?: string;

  // Litigation friend / executor / administrator details
  clientDeceased?: boolean;
  lenderWithdrawalSentAt?: string;
  lenderWithdrawalSentBy?: string;
  multipleRepEmailSentAt?: string;
  multipleRepEmailSentBy?: string;
  multipleRepTaskId?: string;
  litigationFriendDetails?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    dateOfBirth?: string;
    addressLine1?: string;
    addressLine2?: string;
    town?: string;
    county?: string;
    postcode?: string;
    dateObtained?: string;
    methodObtained?: string;
    proofOnFile?: boolean;
    // Executor/Administrator fields
    role?: 'executor' | 'administrator' | 'litigationFriend';
    relationshipToClient?: string;
    deathCertificateStatus?: 'received' | 'outstanding' | '';
    willStatus?: 'received' | 'outstanding' | 'na' | '';
    grantOfProbateStatus?: 'received' | 'outstanding' | 'na' | '';
    letterOfAdministrationStatus?: 'received' | 'outstanding' | 'na' | '';
    executorClientId?: string;
  };

  // DocuSeal document signing
  docusealDocumentsSentAt?: string;
  docusealDocumentsSentTo?: string;
  docusealDocumentsSignedAt?: string;
  docusealDocumentsDeclinedAt?: string;
  docusealDocumentsDeclineReason?: string;
  docusealDocumentsDeclinedDocument?: string;
  docusealSignedDocuments?: Array<{
    documentName: string;
    blobUrl: string;
    blobName: string;
    signedAt: string;
  }>;

  // Lender response to submission (imported from lender batch files, e.g.
  // "Complex Law - Batch 1.2"). ONE ENTRY PER AGREEMENT, not per claim: a claim
  // with three agreements holds three entries, and siblings from the same batch
  // share a receivedDate. The UI renders all of them (see LenderResponseCard) --
  // showing only the newest would hide agreements, and a hidden agreement is a
  // missed claim.
  lenderResponses?: LenderResponse[];

  // Bulk submission fields (lender bulk submission)
  submissionSent?: boolean;
  submissionSentDate?: string;
  submissionLenderEmail?: string;
  submissionLenderName?: string;
  submissionOutcome?: string | null;
  submissionOutcomeDate?: string | null;
  /** The lender's OWN agreement reference, promoted from lenderResponses
   * only when unambiguous (exactly one distinct number among matched
   * entries). What they will recognise in future correspondence. */
  lenderAgreementNumber?: string;

  // LOA Portal submissions
  loaSubmissions?: Array<{
    submittedAt: string;
    loaRequestId: string;
    documentUrl: string;
    submissionData: {
      personalDetails: {
        currentNames: Array<{ firstName: string; lastName: string }>;
        previousNames: Array<{ firstName: string; lastName: string }>;
        fullLegalName: string;
        dateOfBirth: string;
      };
      addresses: {
        currentAddress: { line1?: string; line2?: string; city?: string; postcode?: string };
        previousAddresses: Array<{ line1?: string; city?: string; postcode?: string }>;
      };
      vehicles: Array<{ claimId: string; make: string; model: string; registration: string }>;
      consent: { acknowledged: boolean; chequePaymentConsent?: boolean; version: string };
    };
    signature: {
      pngUrl: string;
      hash: string;
      timestamp: string;
      method: string;
    };
    audit: {
      ipAddress: string;
      userAgent: string;
    };
    previousValues?: {
      firstName?: string;
      lastName?: string;
      dateOfBirth?: string;
      clientEmail?: string;
      address?: {
        line1?: string;
        line2?: string;
        city?: string;
        county?: string;
        postcode?: string;
      };
    };
  }>;
  pendingLoaToken?: {
    token: string;
    loaRequestId: string;
    portalUrl: string;
    createdAt: string;
    expiresAt: string;
    sentAt: string;
  };

  /** Staff notes (Freshdesk #570), oldest first. */
  noteEntries?: ClaimNoteEntry[];
  /** Older free-text notes, shown as "Earlier notes" (still editable). */
  notes?: string | null;

  // Additional fields
  [key: string]: any;
}

export interface ClaimNoteEntry {
  id: string;
  text: string;
  createdAt: string;
  createdBy: string;
  editedAt?: string;
  editedBy?: string;
  edits?: { editedAt: string; editedBy: string; previousText: string }[];
}

export const lookupGeoIp = async (ip: string): Promise<GeoIpResult> => {
  const response = await client.get<GeoIpResult>('/ui/claims/geoip', {
    params: { ip }
  });
  return response.data;
};

// send LOC
export const sendLOC = (claimId: string) =>
  client.post(`/ui/claims/${claimId}/send-loc`);

export const getLOAIdentityReviews = async (
  claimId: string
): Promise<LOAIdentityReviewsResponse> => {
  const response = await client.get<LOAIdentityReviewsResponse>(
    `/ui/claims/${encodeURIComponent(claimId)}/loa-identity-reviews`
  );
  return response.data;
};

export const decideLOAIdentityReview = async (
  claimId: string,
  requestId: string,
  decision: 'approve' | 'reject',
  reason: string
): Promise<{ review: LOAIdentityReviewRecord }> => {
  const response = await client.post<{ review: LOAIdentityReviewRecord }>(
    `/ui/claims/${encodeURIComponent(claimId)}/loa-identity-reviews/${encodeURIComponent(requestId)}/decision`,
    { decision, reason }
  );
  return response.data;
};

export interface LocResendFields {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
  craAgreementAddressLine1: string;
  craAgreementAddressLine2: string;
  craAgreementPostcode: string;
  previousAddresses: string[];
  agreementNumber: string;
  vehicleMake: string;
  vehicleModel: string;
  lenderReference: string;
}

export interface LocResendDraft {
  claimId: string;
  sourceVersion: string;
  fields: LocResendFields;
  lender: { name: string; email: string };
  clientEmail?: string | null;
  previousLoc: { sentAt?: string | null; documentUrl?: string | null };
}

export interface LocResendPreview {
  previewToken: string;
  documentUrl: string;
  blobName: string;
  changedFields: string[];
  lender: { name: string; email: string };
}

export interface LocResendResult {
  message: string;
  claimId: string;
  sentAt: string;
  documentUrl: string;
  blobName: string;
  version: number;
  lenderEmail: string;
  clientNotificationSent: boolean;
}

export const getLocResendDraft = async (claimId: string): Promise<LocResendDraft> =>
  (await client.get<LocResendDraft>(`/ui/claims/${claimId}/resend-loc/draft`)).data;

export const previewLocResend = async (
  claimId: string,
  sourceVersion: string,
  fields: LocResendFields,
): Promise<LocResendPreview> =>
  (await client.post<LocResendPreview>(`/ui/claims/${claimId}/resend-loc/preview`, {
    sourceVersion,
    fields,
  })).data;

export const resendLOC = async (
  claimId: string,
  previewToken: string,
  notifyClient: boolean,
  requestId: string,
): Promise<LocResendResult> =>
  (await client.post<LocResendResult>(`/ui/claims/${claimId}/resend-loc`, {
    previewToken,
    notifyClient,
    requestId,
  })).data;

// Send Multiple Representation clarification email
export const sendMultipleRepEmail = (claimId: string) =>
  client.post<{ message: string; claimId: string; sentBy: string; sentAt: string }>(`/ui/claims/${claimId}/send-multiple-rep-email`);

// Send the Withdrawal of Responsibility email to the claim's mapped lender.
// Same MailerSend template as the automatic withdrawal the cancellation flow
// sends, so the two paths cannot drift apart.
export const sendLenderWithdrawalEmail = (claimId: string) =>
  client.post<{
    message: string;
    claimId: string;
    lenderName: string;
    lenderEmail: string;
    sentBy: string;
    sentAt: string;
    claimStamped: boolean;
    correspondenceLogged: boolean;
  }>(`/ui/claims/${claimId}/send-lender-withdrawal`);

// Send Deceased Client Intro email (estate document request) to the litigation friend / executor
export const sendDeceasedIntroEmail = (claimId: string) =>
  client.post<{ message: string; claimId: string; sentBy: string; sentAt: string }>(`/ui/claims/${claimId}/send-deceased-intro-email`);

// Request LOA - sends portal link email to client.
// `resent` distinguishes "the client was handed the SAME link again" from "a
// new questionnaire was minted". The route has always returned it; the caller
// used to discard it and show one hardcoded success message for both, so
// there was no way to tell which had happened.
export const requestLOA = (claimId: string) =>
  client.post<{ message: string; loaRequestId?: string; resent?: boolean }>(`/ui/claims/${claimId}/request-loa`);

// Risk assessment versions that apply to a claim, oldest first (#571)
export interface RiskAssessmentVersion {
  version: string;
  label: string;
}

export const getRiskAssessmentVersions = (claimId: string) =>
  client.get<{ versions: RiskAssessmentVersion[] }>(`/ui/claims/${claimId}/risk-assessments`);

// Generate risk assessment document (no version = newest that applies)
export const generateRiskAssessment = (claimId: string, version?: string) =>
  client.post<{ url: string; blobName: string; version: string }>(
    `/ui/claims/${claimId}/generate-risk-assessment`,
    version ? { version } : {}
  );

// Freshdesk #677: build the DSAR response letter (.docx) covering all of this
// client's claims. The server also logs it on the claim's timeline. Backend
// CORS hides Content-Disposition, so the filename usually comes from the
// fallback rather than the header.
export const generateDsarDocument = async (
  claimId: string
): Promise<{ blob: Blob; filename: string }> => {
  const response = await client.post<Blob>(
    `/ui/claims/${encodeURIComponent(claimId)}/generate-dsar`,
    {},
    { responseType: 'blob', timeout: 60000 }
  );
  const filename =
    filenameFromContentDisposition(response.headers?.['content-disposition'])
    || dsarFallbackFilename(claimId);
  return { blob: response.data, filename };
};

// Send custom letter
export interface SendLetterData {
  recipientEmail: string;
  recipientName: string;
  recipientAddressLine1: string;
  recipientAddressLine2: string;
  recipientPostcode: string;
  letterContent: string;
}

export const sendLetter = (claimId: string, letterData: SendLetterData) =>
  client.post(`/ui/claims/${claimId}/send-letter`, letterData);

export const generateLetter = (claimId: string, letterData: SendLetterData) =>
  client.post(`/ui/claims/${claimId}/generate-letter`, letterData);

// Cancel claim
export interface AssociatedClaimsResponse {
  hasAssociatedClaims: boolean;
  associatedClaimsCount: number;
  associatedClaims: Array<{
    id: string;
    craFinanceLender?: string;
    createdInApiAt?: string;
  }>;
  userId?: string;
}

export const checkAssociatedClaims = async (claimId: string): Promise<AssociatedClaimsResponse> => {
  const response = await client.get(`/ui/claims/${claimId}/check-associated`);
  return response.data as AssociatedClaimsResponse;
};

// Cancel claim response interface
export interface CancelClaimResponse {
  message: string;
  requiresPayment?: boolean;
  paymentUrl?: string;
  amount?: number;
  claimCount?: number;
  paymentId?: string;
  paymentExpiresAt?: string;
  reinstateLink?: string;
  reinstateToken?: string;
  alreadyCancelled?: string[];
  manualTaskCreated?: boolean;
  taskIds?: string[];
  clClaimIds?: string[];
  /** 70+ pre-cancellation hold (2026-08-18): true means NOTHING was
   * cancelled and the client was NOT emailed -- a review task was raised
   * instead. Never treat as failure. */
  held?: boolean;
  heldClaimIds?: string[];
  holdTaskIds?: string[];
}

/** One message for every surface that alerts a held result -- ClaimsTable and
 * HierarchicalClaimsTable both use this so their wording cannot drift. */
export const heldCancellationAlert = (data: CancelClaimResponse): string =>
  data.message
  || 'This cancellation is on hold for 70+ review. The claims were NOT cancelled '
     + 'and the client has not been emailed — a review task has been created in the Manual lane.';

/** Amendment 3 (19 Aug 2026): the id of the hold task a held result can be
 * reviewed against right now, or null when there is nothing safe to bind
 * the inline "on the phone" questionnaire to. A held response can carry
 * MULTIPLE task ids -- a mixed request returns existing + new (index 0 is
 * then a PRE-EXISTING task, not the one just created) and unidentifiable/
 * malformed claims produce one task per claim -- so this only returns an id
 * when there is exactly one, otherwise the caller must fall back to the
 * plain alert + lane (no inline dialog). Pure and side-effect-free. */
export const heldReviewTaskId = (data: CancelClaimResponse): string | null =>
  (data.holdTaskIds && data.holdTaskIds.length === 1) ? data.holdTaskIds[0] : null;

/** One question for every surface that offers the inline review -- ClaimsTable
 * and HierarchicalClaimsTable both ask this so the wording cannot drift. */
export const PHONE_REVIEW_QUESTION =
  'This client is over 70 and needs an over-70 check — do you have them on the phone?';

export interface CancelClaimRequest {
  reason: string;
  nonCooperationReasonCodes?: string[];
  cancelAllAssociated?: boolean;
  claimIds?: string[];
  bypassPayment?: boolean | null;
  paymentDecision?: 'request' | 'bypass';
  paymentDecisionConfirmed?: boolean;
  /** Optional ISO date (YYYY-MM-DD or a full timestamp) the client actually
   * asked to cancel — e.g. the received date on a cancellation manual-review
   * task. The backend judges the 14-day cooling-off window at this date
   * instead of the processing date; the client's age is still reckoned at
   * now (two clocks, ruling 2026-08-25). Omit it and nothing changes: the
   * backend behaves exactly as it did before this field existed. Garbage is
   * rejected with a 400, never silently ignored. */
  requestReceivedDate?: string;
}

export const cancelClaim = (claimId: string, payload: CancelClaimRequest) =>
  client.post<CancelClaimResponse>(`/ui/claims/${claimId}/cancel`, payload);

// Side-effect-free preview of the cancellation: returns the per-claim charge
// breakdown (with reasonCode + reasonText) so the UI can show *why* each claim
// is or isn't going to be charged before the user commits to cancelling.
export type CancelPreviewReasonCode =
  | 'CHARGE_OLD_LOC_SENT'
  | 'FREE_WITHIN_GRACE'
  | 'FREE_LOC_NOT_SENT'
  | 'FREE_OUTSIDE_REDRESS_SCHEME'
  // 70+ cancellation ring-fence: the client is 70+ (on either
  // dateOfBirth or craDateOfBirth) or has no readable date of birth, so
  // no fee is charged and a manual review task is raised instead of an
  // invoice. Emitted by cancellation_payment_service._classify_claim --
  // it was already reachable while this union still had four members.
  | 'FREE_AGE_RINGFENCED'
  // The client already cancelled this claim inside their 14-day
  // cooling-off window (graceCancellationDate is set): a later reinstate
  // cycle can never re-arm the fee that in-grace cancellation avoided.
  // Emitted by cancellation_payment_service._classify_charge_rules
  // (2026-08-25, Fix B).
  | 'FREE_PRIOR_GRACE_CANCELLATION';

export interface CancelPreviewClaimEntry {
  claimId: string;
  claimRef: string;
  willCharge: boolean;
  amount: number;
  reasonCode: CancelPreviewReasonCode;
  reasonText: string;
  evidence: Record<string, unknown>;
}

export interface CancelPreviewResponse {
  claimBreakdown: CancelPreviewClaimEntry[];
  chargeableCount: number;
  chargeableClaimIds: string[];
  estimatedAmount: number;
  currency: string;
}

export const fetchCancelPreview = (
  claimId: string,
  cancelAllAssociated: boolean,
  requestReceivedDate?: string,
) =>
  client
    .post<Partial<CancelPreviewResponse>>(
      `/ui/claims/${encodeURIComponent(claimId)}/cancel/preview`,
      requestReceivedDate
        ? { cancelAllAssociated, requestReceivedDate }
        : { cancelAllAssociated },
    )
    .then<CancelPreviewResponse>((res) => ({
      claimBreakdown: res.data.claimBreakdown ?? [],
      chargeableCount: res.data.chargeableCount ?? 0,
      chargeableClaimIds: res.data.chargeableClaimIds ?? [],
      estimatedAmount: res.data.estimatedAmount ?? 0,
      currency: res.data.currency ?? 'GBP',
    }));

export type RemainingPaymentAction = 'continue_billing' | 'waive_remaining';

export interface ReinstatementPaymentImpact {
  claimId: string;
  hasPayment: boolean;
  affectsPayment: boolean;
  requiresDecision: boolean;
  blocked: boolean;
  blockReason: string | null;
  paymentId: string | null;
  paymentStatus: string | null;
  currentAmount: number | null;
  remainingAmount: number | null;
  remainingClaimIds: string[];
  remainingClaimCount: number;
  currency: 'GBP' | string;
  /** A manual payment link (#680): keep-billing raises a new manual link and emails nobody. */
  isManualPayment?: boolean;
}

/** Result of the staff decision, returned on the reinstated claim. */
export interface ReinstatementPaymentResult {
  paymentAction?: string;
  remainingAmount?: number;
  replacementPaymentUrl?: string;
  replacementIsManual?: boolean;
}

export const fetchReinstatementPaymentImpact = (claimId: string) =>
  client
    .get<ReinstatementPaymentImpact>(
      `/ui/claims/${encodeURIComponent(claimId)}/uncancel/preview`
    )
    .then(res => res.data);

// Reinstate claim (was previously called uncancelClaim).
// force=true lets an admin override a block (e.g. disabled reinstatement or a non-viable lender).
export const reinstateClaim = (
  claimId: string,
  force?: boolean,
  remainingPaymentAction?: RemainingPaymentAction,
) =>
  client.post<Claim>(`/ui/claims/${encodeURIComponent(claimId)}/uncancel`, {
    force,
    remainingPaymentAction,
  }).then(res => res.data);

// Keep the old function name for backward compatibility
export const uncancelClaim = reinstateClaim;

// Toggle reinstatement disabled flag for a claim. scope picks whether the toggle
// applies to just this claim, all of the client's claims, or a chosen subset.
export type ReinstatementScope = 'single' | 'all' | 'selected';

export interface ToggleReinstatementResponse {
  message: string;
  updatedCount: number;
  updatedClaimIds: string[];
  reinstatementDisabled: boolean;
  scope: ReinstatementScope;
}

export const toggleReinstatement = (
  claimId: string,
  disable: boolean,
  options?: { scope?: ReinstatementScope; claimIds?: string[] },
) =>
  client.post<ToggleReinstatementResponse>(`/ui/claims/${claimId}/toggle-reinstatement`, {
    disable,
    scope: options?.scope,
    claimIds: options?.claimIds,
  }).then(res => res.data);

// Set or clear the do-not-enforce flag on a claim
export const setEnforcementFlag = (claimId: string, payload: { doNotEnforce: boolean; doNotEnforceReason?: string; doNotEnforceNotes?: string }) =>
  client.post<{ message: string; claim: Claim }>(`/ui/claims/${claimId}/enforcement-flag`, payload).then(res => res.data);

// Update client information (name, email, address, phone) - available to all users.
// defendantReference/commissionAmount/commissionType are NOT accepted here -
// they're per-claim commercial terms, edited exclusively via the role-gated
// updateCreditAgreement() below (backend allowed_fields no longer includes them).
export const updateClientInfo = (claimId: string, clientData: {
  title?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  addressLine1?: string;
  addressLine2?: string;
  town?: string;
  county?: string;
  postcode?: string;
  dateOfBirth?: string;
  applyToAllClaims?: boolean;
}) =>
  client.put<{message: string; claim: Claim; updatedFields: string[]; totalUpdated?: number; clientClaimCount?: number}>(`/ui/claims/${claimId}/update-client-info`, clientData).then(res => res.data);

// Update the staff-editable vehicle reg plate (#405). vehicleRegistration is
// per-claim but NOT a credit-agreement fact, so it stays on this unrestricted
// route (any authenticated staff member may edit it) rather than moving to
// the role-gated /credit-agreement route below. craAgreementNumberOriginal
// and craCreditStartDate moved off this route to /credit-agreement.
// Every change is recorded in the hidden auditLog with the acting user.
export const updateAgreementFields = (claimId: string, fields: {
  vehicleRegistration?: string;
}) =>
  client.put<{message: string; claim: Claim; changes?: string[]}>(`/ui/claims/${claimId}/agreement-fields`, fields).then(res => res.data);

// Update one or more credit-agreement fields (admins + credit-agreement-editor role).
// Per-claim only; each changed field is recorded in the hidden auditLog, a
// correspondence memo, and the Notes field (who + from->to), server-side.
// Pass confirmBlankDate: true alongside an empty agreement_dateAgreed to
// confirm clearing that field (it drives charging eligibility elsewhere).
export const updateCreditAgreement = (claimId: string, fields: Record<string, string | boolean>) =>
  client.put<{message: string; claim: Claim; changes: string[]}>(`/ui/claims/${claimId}/credit-agreement`, fields).then(res => res.data);

// Correct just the agreement number - any staff member (the full panel above
// stays role-gated). The server cleans the value, also updates the lender
// agreement number used on letters if the claim has one, and records the
// change (audit, timeline memo, Notes). It sends nothing and does not resend
// the letter of claim. `warning` is a plain-words "please double-check" note.
export const updateAgreementNumber = (claimId: string, agreementNumber: string) =>
  client.put<{message: string; changed: boolean; claim: Claim; changes: string[]; warning: string | null}>(
    `/ui/claims/${claimId}/agreement-number`, { agreementNumber },
  ).then(res => res.data);

// Get credit check report HTML
export const getCreditCheckReportUrl = (claimId: string) => {
  const token = localStorage.getItem('token');
  const url = `${API_BASE_URL}/ui/claims/${claimId}/credit-check-report`;
  if (token) {
    // The backend for this endpoint is specifically configured to check for a token in the query params
    return `${url}?token=${token}`;
  }
  return url;
};

// Test credit check URL
export const testCreditCheckUrl = (claimId: string) =>
  client.get<{
    success: boolean;
    url?: string;
    status_code?: number;
    content_type?: string;
    content_length?: number;
    is_json?: boolean;
    data_preview?: string;
    top_level_keys?: string[];
    error?: string;
    error_type?: string;
    content_preview?: string;
  }>(`/ui/claims/${claimId}/test-credit-check-url`).then(res => res.data);

// Get credit check summary
export const getCreditCheckSummary = (claimId: string) =>
  client.get<{
    claimId: string;
    provider?: string;
    formatType?: string;
    summary: {
      overall_decision: string;
      identity_verified: boolean;
      credit_accounts_found: number;
      total_balance: string;
      worst_status: string;
      pep_sanctions_clear: boolean;
      electoral_roll_confirmed: boolean;
      decision_timestamp: string | null;
    };
    applicant: {
      name: string;
      date_of_birth: string;
      age: string;
      address: string;
    };
    creditAccounts: Array<{
      account_number: string;
      lender: string;
      account_type: string;
      start_date: string;
      status: string;
      balance: string;
      payment: string;
      payment_frequency: string;
      worst_status: string;
      settlement_date: string | null;
    }>;
    electoralRoll: {
      confirmed: boolean;
      years_at_address: string;
      household_members: Array<{
        name: string;
        registered_since: string;
      }>;
    };
    reportAvailable: boolean;
    /** Set when this claim has no report and the summary is this other claim's (its .001). */
    reportFromClaimId?: string | null;
    reportNotice?: string | null;
    message?: string;
  }>(`/ui/claims/${claimId}/credit-check-summary`).then(res => res.data);

export interface ClaimAmlSummary {
  claimId: string;
  summary: {
    status?: string;
    identityResult?: string | null;
    identityScore?: string | null;
    matchLevel?: string | null;
    numPrimaryChecks?: string | null;
    numCorroborativeChecks?: string | null;
    warnings: Array<{ label: string; value: string }>;
    input: any;
    raw: any;
  };
  reportAvailable: boolean;
}

export const getClaimAmlSummary = (claimId: string) =>
  client.get<ClaimAmlSummary>(`/ui/claims/${claimId}/aml-summary`).then((res) => res.data);

export const fetchClaim = (claimId: string) =>
  client.get<Claim>(`/ui/claims/${claimId}`).then(res => res.data);

export interface ClaimPage {
  items: Claim[];
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface ClaimFilters {
  showAuditedOnly?: boolean;
  showDeceasedOnly?: boolean;
  statusFilter?: string;
  sortBy?: 'name' | 'date';
  sortOrder?: 'asc' | 'desc';
}

export const getClaim = async (claimId: string): Promise<Claim> => {
  try {
    const response = await client.get<Claim>(`/ui/claims/${claimId}`);
    // Debug: Log the raw API response
    const firstItem = response.data.correspondence?.[0];
    console.log('🔍 getClaim API response for', claimId, {
      hasCorrespondence: 'correspondence' in response.data,
      correspondenceLength: response.data.correspondence?.length,
      firstItem: firstItem && 'subject' in firstItem ? firstItem.subject : firstItem && 'title' in firstItem ? firstItem.title : undefined
    });
    return response.data;
  } catch (error: any) {
    console.error('Failed to fetch claim:', error);
    throw error;
  }
};

export async function fetchClaims(
  page = 1,
  pageSize = 100,
  search = "",
  filters?: ClaimFilters
): Promise<ClaimPage> {
  const res = await client.get<ClaimPage>("/ui/claims", {
    params: {
      page,
      pageSize,
      search,
      ...(filters?.showAuditedOnly && { audited: true }),
      ...(filters?.showDeceasedOnly && { deceased: true }),
      ...(filters?.statusFilter && filters.statusFilter !== 'all' && { status: filters.statusFilter }),
      ...(filters?.sortBy && { sortBy: filters.sortBy }),
      ...(filters?.sortOrder && { sortOrder: filters.sortOrder })
    }
  });
  return res.data;
}

export const getClaimKeyDates = async (claimId: string): Promise<ClaimKeyDatesResponse> => {
  const response = await client.get<ClaimKeyDatesResponse>(`/ui/claims/${claimId}/key-dates`);
  return response.data;
};

export const updateClaimKeyDates = async (
  claimId: string,
  updates: KeyDatesUpdatePayload
): Promise<ClaimKeyDatesResponse> => {
  const response = await client.put<ClaimKeyDatesResponse>(`/ui/claims/${claimId}/key-dates`, updates);
  return response.data;
};

export const getLenderBulkSubmissionDetails = async (
  claimId: string
): Promise<LenderBulkSubmissionResponse> => {
  const response = await client.get<LenderBulkSubmissionResponse>(
    `/ui/claims/${claimId}/lender-bulk-submission`
  );
  return response.data;
};

export const updateLenderBulkSubmissionDetails = async (
  claimId: string,
  updates: LenderBulkSubmissionUpdatePayload
): Promise<LenderBulkSubmissionResponse> => {
  const response = await client.put<LenderBulkSubmissionResponse>(
    `/ui/claims/${claimId}/lender-bulk-submission`,
    { lenderBulkSubmission: updates }
  );
  return response.data;
};

// File management APIs
export interface ClaimFile {
  blob_name: string;
  url: string;
  filename: string;
  content_type: string;
  size: number;
  uploaded_by: string;
  uploaded_at: string;
  description: string;
}

export const listClaimFiles = (claimId: string) =>
  client.get<{ files: ClaimFile[]; count: number }>(`/ui/claims/${claimId}/files`).then(res => res.data);

interface UploadFileMetadata {
  units: number;
  description: string;
  sender?: string;
  recipient?: string;
  email_date?: string;
  direction?: string;
}

export const uploadClaimFile = (claimId: string, file: File, metadata: UploadFileMetadata) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('units', String(metadata.units));
  formData.append('description', metadata.description);
  if (metadata.sender) {
    formData.append('sender', metadata.sender);
  }
  if (metadata.recipient) {
    formData.append('recipient', metadata.recipient);
  }
  if (metadata.email_date) {
    formData.append('email_date', metadata.email_date);
  }
  if (metadata.direction) {
    formData.append('direction', metadata.direction);
  }

  return client.post<{ message: string; file: ClaimFile }>(`/ui/claims/${claimId}/files`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }).then(res => res.data);
};

export const deleteClaimFile = (claimId: string, blobName: string) =>
  client.delete(`/ui/claims/${claimId}/files/${encodeURIComponent(blobName)}`).then(res => res.data);

// Fetch claim document as base64 through API to avoid CORS
export const fetchClaimDocument = async (claimId: string, documentType: 'foa' | 'dba' | 'loc'): Promise<{ content: string; filename: string }> => {
  console.log('🎯 fetchClaimDocument called - Version 2024-01-24-v2');
  console.log('🎯 Parameters:', { claimId, documentType });
  console.log('🎯 Full URL:', `${API_BASE_URL}/ui/claims/${claimId}/document/${documentType}`);
  
  const response = await client.get<ArrayBuffer>(`/ui/claims/${claimId}/document/${documentType}`, {
    responseType: 'arraybuffer'
  });
  
  // Convert arraybuffer to base64
  const base64 = btoa(
    new Uint8Array(response.data as ArrayBuffer)
      .reduce((data, byte) => data + String.fromCharCode(byte), '')
  );
  
  // Get filename from content-disposition header or use default
  const contentDisposition = response.headers['content-disposition'];
  let filename = `${documentType}.pdf`;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="(.+)"/);
    if (match) filename = match[1];
  }
  
  return { content: base64, filename };
};

// Audit interfaces and functions
export interface AuditData {
  // DBA Section
  dba_signed_and_on_file?: boolean;
  dba_printed_name_signature_match_file?: boolean;
  dba_address_match_file?: boolean;
  dba_terms_of_business_agree_to_dba?: boolean;
  
  // FOA Section  
  foa_signed_and_on_file?: boolean;
  foa_and_dba_signature_match?: boolean;
  
  // KYC Section
  kyc_credit_check_on_file?: boolean;
  kyc_name_match?: boolean;
  kyc_address_match?: boolean;
  kyc_pep_sanctions_clear?: boolean;
  kyc_electoral_roll_confirmed?: boolean;
  kyc_active_credit_accounts_found?: boolean;
  
  // Additional notes
  notes?: string;
}

export interface Audit {
  id: string;
  claimId: string;
  auditedBy: string;
  auditedAt: string;
  documentGenerated: boolean;
  documentUrl?: string;
  documentGeneratedAt?: string;
  notes?: string;
  // All the audit fields from AuditData
  [key: string]: any;
}

// Create audit for a claim
export const createAudit = (claimId: string, auditData: AuditData) =>
  client.post<{message: string; auditId: string; audit: Audit}>(`/ui/claims/${claimId}/audit`, auditData).then(res => res.data);

// Get all audits for a claim
export const getClaimAudits = (claimId: string) =>
  client.get<{audits: Audit[]; count: number}>(`/ui/claims/${claimId}/audit`).then(res => res.data);

// Generate audit document
export const generateAuditDocument = (claimId: string, auditId: string) =>
  client.post<{message: string; documentUrl: string}>(`/ui/claims/${claimId}/audit/${auditId}/generate-document`).then(res => res.data);

// Add correspondence to a claim
export const addCorrespondence = (claimId: string, correspondence: Partial<EmailCorrespondence | PhoneCorrespondence | MemoCorrespondence>) =>
  client.post<{message: string; correspondenceId: string; correspondence: Correspondence}>(`/ui/claims/${claimId}/correspondence`, correspondence).then(res => res.data);

// Temporary debug function - remove after fixing
export const debugBackendConnection = async () => {
  try {
    console.log('🧪 Testing backend connection...');
    
    // Test 1: Basic backend health
    const healthResponse = await client.get('/health').catch(e => ({ error: e.message }));
    console.log('🔹 Backend health:', healthResponse);
    
    // Test 2: Auth endpoint
    const authTest = await client.get('/ui/claims').catch(e => ({ error: e.message, status: e.response?.status }));
    console.log('🔹 Auth test:', authTest);
    
    // Test 3: Get a specific claim to test credit check
    const claimsResponse = await fetchClaims(1, 5).catch(e => ({ error: e.message }));
    console.log('🔹 Sample claims:', claimsResponse);
    
    if (claimsResponse && 'items' in claimsResponse && claimsResponse.items.length > 0) {
      const sampleClaim = claimsResponse.items[0];
      console.log('🔹 Sample claim for credit check:', sampleClaim.id, 'has valid8responseUrl:', !!sampleClaim.valid8responseUrl);
      
      if (sampleClaim.valid8responseUrl) {
        // Test credit check
        const creditTest = await getCreditCheckSummary(sampleClaim.id).catch(e => ({ 
          error: e.message, 
          status: e.response?.status,
          data: e.response?.data 
        }));
        console.log('🔹 Credit check test:', creditTest);
      }
    }
    
  } catch (error) {
    console.error('🚨 Backend debug failed:', error);
  }
};

// Add a note (Freshdesk #570): stored as its own entry with the server's
// time and the author. Not retried automatically -- a retry after a
// timeout could add the same note twice.
export interface AddClaimNoteResponse {
  note: ClaimNoteEntry;
  noteEntries: ClaimNoteEntry[];
}

export const addClaimNote = async (claimId: string, text: string): Promise<AddClaimNoteResponse> => {
  const response = await client.post<AddClaimNoteResponse>(`/ui/claims/${claimId}/notes`, { text });
  return response.data;
};

// Edit one note; the server records who edited it, when, and the old text.
export const editClaimNote = async (claimId: string, noteId: string, text: string): Promise<ClaimNoteEntry[]> => {
  const response = await client.put<{ noteEntries: ClaimNoteEntry[] }>(`/ui/claims/${claimId}/notes/${noteId}`, { text });
  return response.data.noteEntries;
};

// Save edits to the older free-text notes (recorded in the claim's audit log).
export const updateClaimNotes = async (claimId: string, notes: string): Promise<void> => {
  await client.put(`/ui/claims/${claimId}/update`, { notes });
};

// Generate fresh SAS URL for blob documents
export interface GenerateSasUrlRequest {
  blobUrl?: string;
  blobName?: string;
}

export interface GenerateSasUrlResponse {
  sasUrl: string;
  blobName: string;
  expiresIn: string;
}

export const generateDocumentSasUrl = async (
  request: GenerateSasUrlRequest
): Promise<GenerateSasUrlResponse> => {
  const response = await client.post<GenerateSasUrlResponse>(
    '/ui/claims/documents/sas-url',
    request
  );
  return response.data;
};

// List LOC documents from blob storage
export interface LocBlobDocument {
  blobName: string;
  url: string;
  lastModified: string | null;
}

export const listLocDocuments = async (
  claimId: string
): Promise<{ documents: LocBlobDocument[]; count: number }> => {
  const response = await client.get<{ documents: LocBlobDocument[]; count: number }>(
    `/ui/claims/${claimId}/loc-documents`
  );
  return response.data;
};

// List FOA documents for version history
export const listFoaDocuments = async (
  claimId: string
): Promise<{ documents: LocBlobDocument[]; count: number }> => {
  const response = await client.get<{ documents: LocBlobDocument[]; count: number }>(
    `/ui/claims/${claimId}/foa-documents`
  );
  return response.data;
};

// Create claim types and function
export interface CreateClaimPayload {
  clientId?: string;
  useNewClReference?: boolean;  // When true, creates fresh CL reference even for existing clients
  title?: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  previouslyKnownNames?: string[];
  addressLine1: string;
  addressLine2?: string;
  town?: string;
  county?: string;
  city?: string;
  postcode: string;
  previousAddresses?: string[];
  craFinanceLender: string;
  craAgreementNumberOriginal: string;
  craCompanyType?: string;
  craCreditStartDate?: string;
  craCreditEndDate?: string;
  defendantReference?: string;
  commissionAmount?: number;
  commissionType?: string;
  newLenderEmail?: string;
}

export const createClaim = async (payload: CreateClaimPayload): Promise<Claim> => {
  const response = await client.post<{ message: string; claim: Claim }>('/ui/claims', payload);
  return response.data.claim;
};

// Split claims types and functions
export interface RelatedClaim {
  id: string;
  proclaimReference?: string;
  status?: string;
  craFinanceLender?: string;
  createdInApiAt?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface RelatedClaimsResponse {
  clientId: string;
  claims: RelatedClaim[];
  count: number;
}

export interface SplitClaimsPayload {
  claimIds: string[];
  newClientData: {
    email: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    postcode?: string;
  };
}

export interface SplitClaimsResponse {
  success: boolean;
  newClientId: string;
  newClClientNumber: number;
  splitClaimCount: number;
  message?: string;
}

export const getRelatedClaims = async (claimId: string): Promise<RelatedClaimsResponse> => {
  const response = await client.get<RelatedClaimsResponse>(`/ui/claims/${claimId}/related-claims`);
  return response.data;
};

export const splitClaims = async (payload: SplitClaimsPayload): Promise<SplitClaimsResponse> => {
  const response = await client.post<SplitClaimsResponse>('/ui/claims/split', payload);
  return response.data;
};

export const getDuplicationAudit = async (
  claimId: string
): Promise<{ claimId: string; duplicationAudit: DuplicationAudit | null }> => {
  const response = await client.get<{ claimId: string; duplicationAudit: DuplicationAudit | null }>(
    `/ui/claims/${claimId}/duplication-audit`
  );
  return response.data;
};

export const saveDuplicationAudit = async (
  claimId: string,
  data: Omit<DuplicationAudit, 'completedBy' | 'completedAt' | 'lastUpdatedBy' | 'lastUpdatedAt'>
): Promise<{ message: string; duplicationAudit: DuplicationAudit }> => {
  const response = await client.put<{ message: string; duplicationAudit: DuplicationAudit }>(
    `/ui/claims/${claimId}/duplication-audit`,
    { duplicationAudit: data }
  );
  return response.data;
};

export { client };
